import { ToolItem } from '../types';

export interface FinanceToolItem extends ToolItem {
  financeCategory: 'loans-mortgages' | 'investment-savings' | 'business-profit' | 'income-salary' | 'personal-finance';
  financeCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const FINANCE_CATEGORIES_TABS = [
  { id: 'all', label: 'All Finance Tools', icon: 'DollarSign' },
  { id: 'loans-mortgages', label: 'Loans & Mortgages', icon: 'Building' },
  { id: 'investment-savings', label: 'Investments & Savings', icon: 'TrendingUp' },
  { id: 'business-profit', label: 'Business & Sales', icon: 'Briefcase' },
  { id: 'income-salary', label: 'Income & Salary', icon: 'Wallet' },
  { id: 'personal-finance', label: 'Personal & Expenses', icon: 'PiggyBank' }
];

export const FINANCE_TOOLS_DATA: FinanceToolItem[] = [
  // --- LOANS & MORTGAGES (4 Tools) ---
  {
    id: 'emi-calculator',
    name: 'EMI Calculator',
    description: 'Calculate Equated Monthly Installments for home, car, or personal loans with payment schedules.',
    financeCategory: 'loans-mortgages',
    financeCategoryLabel: 'Loans & Mortgages',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Calculator',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'loan-calculator',
    name: 'Loan Calculator',
    description: 'Determine total interest, monthly payment, and amortization schedule for any loan amount.',
    financeCategory: 'loans-mortgages',
    financeCategoryLabel: 'Loans & Mortgages',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Building',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'mortgage-calculator',
    name: 'Mortgage Calculator',
    description: 'Estimate home loan payments including principal, interest, property taxes, and insurance.',
    financeCategory: 'loans-mortgages',
    financeCategoryLabel: 'Loans & Mortgages',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Home',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'debt-payoff-calculator',
    name: 'Debt Payoff Calculator',
    description: 'Plan debt payoff strategies using snowball or avalanche methods to become debt-free faster.',
    financeCategory: 'loans-mortgages',
    financeCategoryLabel: 'Loans & Mortgages',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'ShieldAlert',
    isPopular: false,
    actionType: 'generic'
  },

  // --- INVESTMENTS & SAVINGS (8 Tools) ---
  {
    id: 'simple-interest-calculator',
    name: 'Simple Interest Calculator',
    description: 'Calculate simple interest earnings on principal deposits over days, months, or years.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Percent',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'compound-interest-calculator',
    name: 'Compound Interest Calculator',
    description: 'Forecast wealth growth with daily, monthly, or annual compounding and recurring deposits.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'TrendingUp',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'savings-calculator',
    name: 'Savings Calculator',
    description: 'Calculate how much your savings balance will accumulate over time with regular contributions.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'PiggyBank',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'retirement-calculator',
    name: 'Retirement Calculator',
    description: 'Estimate required nest egg size and monthly savings needed for a comfortable retirement.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Award',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'investment-calculator',
    name: 'Investment Calculator',
    description: 'Project investment growth across stocks, mutual funds, SIPs, and fixed deposits.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'BarChart2',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'roi-calculator',
    name: 'ROI Calculator',
    description: 'Measure return on investment percentage and net profit across business or financial assets.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'PieChart',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'savings-goal-calculator',
    name: 'Savings Goal Calculator',
    description: 'Find out how long it takes to reach specific monetary targets like buying a house or car.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Target',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'net-worth-calculator',
    name: 'Net Worth Calculator',
    description: 'Track overall financial health by subtracting total liabilities from total assets.',
    financeCategory: 'investment-savings',
    financeCategoryLabel: 'Investments & Savings',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'DollarSign',
    isPopular: true,
    actionType: 'generic'
  },

  // --- BUSINESS & SALES (9 Tools) ---
  {
    id: 'profit-calculator',
    name: 'Profit Calculator',
    description: 'Calculate gross and net business profit margins based on revenue and expenses.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Coins',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'margin-calculator',
    name: 'Margin Calculator',
    description: 'Calculate profit margin percentage and gross profit from cost and selling prices.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Percent',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'markup-calculator',
    name: 'Markup Calculator',
    description: 'Determine required selling prices based on wholesale cost and desired markup percentage.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Tag',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'break-even-calculator',
    name: 'Break-even Calculator',
    description: 'Determine unit sales volume required to cover fixed and variable business costs.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Scale',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'discount-calculator',
    name: 'Discount Calculator',
    description: 'Quickly calculate final sale prices and total savings during retail discount offers.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'ShoppingBag',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'gst-calculator',
    name: 'GST Calculator',
    description: 'Calculate inclusive or exclusive Goods and Services Tax amounts for invoice billing.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Receipt',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'vat-calculator',
    name: 'VAT Calculator',
    description: 'Add or remove Value Added Tax from product costs across standard VAT percentages.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'FileText',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'sales-tax-calculator',
    name: 'Sales Tax Calculator',
    description: 'Compute state or local sales tax and total checkout price for purchases.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'CreditCard',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'commission-calculator',
    name: 'Commission Calculator',
    description: 'Calculate sales agent commission payouts based on tiered or flat rate percentages.',
    financeCategory: 'business-profit',
    financeCategoryLabel: 'Business & Sales',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Briefcase',
    isPopular: false,
    actionType: 'generic'
  },

  // --- INCOME & SALARY (4 Tools) ---
  {
    id: 'salary-calculator',
    name: 'Salary Calculator',
    description: 'Convert annual salary into monthly, bi-weekly, weekly, or hourly net income figures.',
    financeCategory: 'income-salary',
    financeCategoryLabel: 'Income & Salary',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Wallet',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'overtime-calculator',
    name: 'Overtime Calculator',
    description: 'Calculate time-and-a-half or double-time overtime pay for extra working hours.',
    financeCategory: 'income-salary',
    financeCategoryLabel: 'Income & Salary',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Clock',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'hourly-wage-calculator',
    name: 'Hourly Wage Calculator',
    description: 'Determine equivalent annual earnings based on hourly rates and weekly work hours.',
    financeCategory: 'income-salary',
    financeCategoryLabel: 'Income & Salary',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'DollarSign',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'inflation-calculator',
    name: 'Inflation Calculator',
    description: 'Calculate purchasing power loss and historical price changes due to inflation.',
    financeCategory: 'income-salary',
    financeCategoryLabel: 'Income & Salary',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'TrendingDown',
    isPopular: true,
    actionType: 'generic'
  },

  // --- PERSONAL & EXPENSES (5 Tools) ---
  {
    id: 'tip-calculator',
    name: 'Tip Calculator',
    description: 'Calculate restaurant or service tip amounts and total bill instantly.',
    financeCategory: 'personal-finance',
    financeCategoryLabel: 'Personal & Expenses',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Utensils',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'split-bill-calculator',
    name: 'Split Bill Calculator',
    description: 'Split group dining or shared expense bills evenly among friends with tip included.',
    financeCategory: 'personal-finance',
    financeCategoryLabel: 'Personal & Expenses',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Users',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'currency-converter-manual',
    name: 'Currency Converter (Manual)',
    description: 'Convert currency amounts using customizable exchange rates for travel and trade.',
    financeCategory: 'personal-finance',
    financeCategoryLabel: 'Personal & Expenses',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'RefreshCw',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'budget-planner',
    name: 'Budget Planner',
    description: 'Allocate monthly income across essentials, savings, and discretionary spending (50/30/20 rule).',
    financeCategory: 'personal-finance',
    financeCategoryLabel: 'Personal & Expenses',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'PieChart',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'expense-tracker-local',
    name: 'Expense Tracker (Local)',
    description: 'Log daily expenses locally in your browser to monitor personal cash flows.',
    financeCategory: 'personal-finance',
    financeCategoryLabel: 'Personal & Expenses',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'ListOrdered',
    isPopular: false,
    actionType: 'generic'
  }
];
